import { useState, useRef } from "react";
import * as XLSX from "xlsx";
import useMsaList from "../../hooks/useMsaList";
import { STAGES } from "../../constants/dealStages";
import { fmt } from "../../utils/formatters";
import { callClaudeWithMessages } from "../../services/claude";
import { extractTextFromFile, pdfToImages } from "../../supabase";
import { buildOMExtractionPrompt, fmtFileSize, fileIcon, UploadZone, googleMapsUrl } from "./DealRoom";
import { inputCls, Field } from "../common/FormHelpers";
import PlacesAutocompleteInput from "../common/PlacesAutocompleteInput";
import { parseDealName } from "../../utils/parseDealName";

const EMPTY_FORM = {
  dealName: "",
  addresses: [""],
  market: "South Florida",
  squareFeet: "",
  askingPrice: "",
  capRate: "",
  stage: "Screening",
  sourceNotes: "",
  thesis: "",
  openQuestions: "",
  redFlags: "",
  underwrittenPrice: "",
  leveredIrr: "",
  equityMultiple: "",
  trendedYoc: "",
  untrendedYoc: "",
  averageCoc: "",
};

// The sentinel value used in the <select> to trigger the custom-market input
const ADD_CUSTOM = "__add_custom__";

// initialValues — pre-filled field values (from OM extraction); falls back to EMPTY_FORM
// omFile        — File object from the OM; pre-queued as a pending document
// omBanner      — filename string; if set, shows a "review before saving" banner
function AddDealForm({ onSave, onCancel, customMarkets, onAddCustomMarket, initialValues, omFile, omBanner, onOpenExisting }) {
  const [form, setForm] = useState(initialValues ?? EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  // Set once the user has seen the duplicate warning and chosen to proceed —
  // insertDeal refuses a look-alike unless the intent is explicit.
  const [allowDuplicate, setAllowDuplicate] = useState(false);
  // Controls whether the "type a custom market" input is visible
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [customMarketInput, setCustomMarketInput] = useState("");
  // Files queued to upload after the deal is saved (we need the deal_id first).
  // Pre-populate with the OM file if one was passed in.
  const [pendingFiles, setPendingFiles] = useState(omFile ? [omFile] : []);
  // Property photo — kept separate from pendingFiles so it can be stored to photo_path
  const photoInputRef = useRef(null);
  const [photoFile, setPhotoFile]       = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);

  function handlePhotoSelect(file) {
    if (!file) return;
    if (photoPreview) URL.revokeObjectURL(photoPreview);
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
  }

  function removePhoto() {
    if (photoPreview) URL.revokeObjectURL(photoPreview);
    setPhotoFile(null);
    setPhotoPreview(null);
  }

  // Markets come from the msa_definitions table (see Settings → MSAs & Counties).
  // customMarkets is preserved for session-scoped ad-hoc entries; anything the
  // user types is unioned in and appears at the top of the dropdown next time.
  const { msas: msaList } = useMsaList([form.market, ...customMarkets]);
  const allMarkets = [...new Set([...msaList, ...customMarkets])];

  function set(field, value) {
    setForm((f) => ({ ...f, [field]: value }));
    setErrors((e) => ({ ...e, [field]: undefined }));
  }

  // Address list helpers
  function setAddress(idx, value) {
    setForm((f) => {
      const arr = [...f.addresses];
      arr[idx] = value;
      return { ...f, addresses: arr };
    });
    setErrors((e) => ({ ...e, addresses: undefined }));
    setAllowDuplicate(false);
    setSaveError(null);
  }

  function addAddress() {
    if (form.addresses.length >= 10) return;
    setForm((f) => ({ ...f, addresses: [...f.addresses, ""] }));
  }

  function removeAddress(idx) {
    setForm((f) => ({ ...f, addresses: f.addresses.filter((_, i) => i !== idx) }));
  }

  function handleMarketChange(value) {
    if (value === ADD_CUSTOM) {
      // Show the custom input instead of setting the market value
      setShowCustomInput(true);
      setCustomMarketInput("");
    } else {
      set("market", value);
      setShowCustomInput(false);
    }
  }

  function commitCustomMarket() {
    const trimmed = customMarketInput.trim();
    if (!trimmed) return;
    onAddCustomMarket(trimmed);   // bubble up to App so it persists in session
    set("market", trimmed);       // select it in the form
    setShowCustomInput(false);
    setCustomMarketInput("");
  }

  function validate() {
    const errs = {};
    if (!form.addresses[0]?.trim()) errs.addresses = "Required";
    if (!form.squareFeet || isNaN(Number(form.squareFeet))) errs.squareFeet = "Enter a number";
    // askingPrice and capRate are optional — only validate if the user typed something
    if (form.askingPrice && isNaN(Number(form.askingPrice))) errs.askingPrice = "Enter a number";
    if (form.capRate && isNaN(Number(form.capRate))) errs.capRate = "Enter a number";
    return errs;
  }

  async function handleSave() {
    const errs = validate();
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }
    setSaving(true);
    setSaveError(null);
    // Prepend deal name to source_notes if provided
    let finalNotes = form.sourceNotes.trim();
    if (form.dealName.trim()) {
      finalNotes = "Name: " + form.dealName.trim() + (finalNotes ? "\n" + finalNotes : "");
    }

    await onSave({
      addresses: form.addresses.filter((a) => a.trim()),
      market: form.market,
      squareFeet: Number(form.squareFeet),
      askingPrice: form.askingPrice ? Number(form.askingPrice) : null,
      capRate: form.capRate ? Number(form.capRate) : 0,
      stage: form.stage,
      sourceNotes: finalNotes,
      thesis: form.thesis.trim(),
      openQuestions: form.openQuestions.trim(),
      redFlags: form.redFlags.trim(),
      underwrittenPrice: form.underwrittenPrice ? Number(form.underwrittenPrice) : null,
      leveredIrr:        form.leveredIrr        ? Number(form.leveredIrr)        : null,
      equityMultiple:    form.equityMultiple    ? Number(form.equityMultiple)    : null,
      trendedYoc:        form.trendedYoc        ? Number(form.trendedYoc)        : null,
      untrendedYoc:      form.untrendedYoc      ? Number(form.untrendedYoc)      : null,
      averageCoc:        form.averageCoc        ? Number(form.averageCoc)        : null,
    }, setSaveError, pendingFiles, photoFile, { allowDuplicate });
    setSaving(false);
  }

  return (
    <div className="min-h-screen bg-[#242424] text-white">
      <header className="border-b border-[rgba(58,58,58,0.6)] bg-[rgba(36,36,36,0.95)] backdrop-blur sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center gap-4">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-md bg-[#e93529] flex items-center justify-center">
              <span className="text-[#333333] font-black text-sm">PP</span>
            </div>
            <span className="text-lg font-bold tracking-tight">
              PPUSA <span className="text-[#f04f44]">Deal Brain</span>
            </span>
          </div>
          <div className="h-4 w-px bg-[#3a3a3a]" />
          <span className="text-sm text-[#8a8a8a]">{omBanner ? "Review OM" : "New Deal"}</span>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-8">
        <h2 className="text-xl font-bold mb-6">{omBanner ? "Review Extracted Deal" : "Add a Deal"}</h2>

        {/* Banner shown when the form was pre-filled from an OM */}
        {omBanner && (
          <div className="mb-5 flex items-start gap-3 bg-[rgba(233,53,41,0.1)] border border-[rgba(233,53,41,0.3)] rounded-xl px-4 py-3">
            <span className="text-lg leading-none flex-shrink-0">📄</span>
            <div>
              <p className="text-sm font-semibold text-[#f04f44]">Fields extracted from: {omBanner}</p>
              <p className="text-xs text-[#8a8a8a] mt-0.5">
                Review and edit all fields before saving. The OM will be attached to the deal automatically.
              </p>
            </div>
          </div>
        )}

        <div className="flex flex-col gap-5">
          {/* Property photo picker */}
          <div>
            <label className="block text-xs font-semibold text-[#8a8a8a] uppercase tracking-wider mb-1.5">
              Property Photo
            </label>
            <div className="flex items-start gap-4">
              <div
                onClick={() => photoInputRef.current?.click()}
                className="relative flex-shrink-0 rounded-xl border-2 border-dashed border-[#4a4a4a] hover:border-[rgba(233,53,41,0.6)] overflow-hidden cursor-pointer transition-colors"
                style={{ width: 160, height: 160 }}
              >
                {photoPreview ? (
                  <>
                    <img src={photoPreview} alt="Property preview" className="w-full h-full object-cover" />
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); removePhoto(); }}
                      className="absolute top-1 right-1 bg-[rgba(36,36,36,0.8)] text-white rounded-full w-5 h-5 flex items-center justify-center text-xs hover:bg-[rgba(196,45,34,0.8)] transition-colors leading-none"
                    >×</button>
                  </>
                ) : (
                  <div className="flex flex-col items-center justify-center h-full text-[#8a8a8a] gap-2 select-none">
                    <span className="text-3xl">📷</span>
                    <span className="text-xs">Add Photo</span>
                  </div>
                )}
              </div>
              <p className="text-xs text-[#8a8a8a] mt-1 leading-relaxed">
                Optional. Upload a photo of the property — it will appear on the deal card and in the Deal Room.
                <br />JPG, PNG, or WebP.
              </p>
            </div>
            <input
              ref={photoInputRef}
              type="file"
              accept=".jpg,.jpeg,.png,.webp"
              className="hidden"
              onChange={(e) => e.target.files?.[0] && handlePhotoSelect(e.target.files[0])}
            />
          </div>

          <Field label="Deal Name">
            <input type="text" value={form.dealName} onChange={(e) => set("dealName", e.target.value)}
              placeholder="e.g. Weston Business Park" className={inputCls()} />
          </Field>

          <Field label="Property Address(es)" error={errors.addresses} required>
            <div className="flex flex-col gap-2">
              {form.addresses.map((addr, idx) => (
                <div key={idx} className="flex gap-2 items-center">
                  <PlacesAutocompleteInput
                    value={addr}
                    onChange={(v) => setAddress(idx, v)}
                    onSelect={({ address }) => setAddress(idx, address)}
                    placeholder={idx === 0 ? "e.g. 3401 NW 82nd Ave, Doral, FL 33122" : "e.g. 456 Oak Ave, Tampa, FL 33602"}
                    className={inputCls(idx === 0 ? errors.addresses : undefined) + " flex-1"}
                  />
                  {idx > 0 && (
                    <button
                      type="button"
                      onClick={() => removeAddress(idx)}
                      className="text-[#8a8a8a] hover:text-[#f04f44] text-base flex-shrink-0 transition-colors leading-none"
                    >×</button>
                  )}
                </div>
              ))}
            </div>
            <div className="flex items-center justify-between mt-1.5">
              {form.addresses.length < 10 && (
                <button
                  type="button"
                  onClick={addAddress}
                  className="text-xs text-[#f04f44] hover:text-[#f04f44] transition-colors"
                >
                  + Add Address
                </button>
              )}
              <span className="text-xs text-[#8a8a8a] ml-auto">
                {form.addresses.length} of 10 address{form.addresses.length !== 1 ? "es" : ""}
              </span>
            </div>
            {form.addresses[0]?.trim() && (
              <a
                href={googleMapsUrl(form.addresses.filter(Boolean))}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-1.5 inline-flex items-center gap-1 text-xs text-[#f04f44] hover:text-[#f04f44] transition-colors"
              >
                View on Google Maps →
              </a>
            )}
          </Field>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Market">
              {/* If the user chose "+ Add Custom Market", show a text input instead */}
              {showCustomInput ? (
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={customMarketInput}
                    onChange={(e) => setCustomMarketInput(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && commitCustomMarket()}
                    placeholder="e.g. Phoenix"
                    autoFocus
                    className={inputCls()}
                  />
                  <button
                    type="button"
                    onClick={commitCustomMarket}
                    className="px-3 py-2 bg-[#e93529] hover:bg-[#f04f44] text-[#333333] font-semibold text-sm rounded-lg transition-colors flex-shrink-0"
                  >
                    Add
                  </button>
                </div>
              ) : (
                <select
                  value={form.market}
                  onChange={(e) => handleMarketChange(e.target.value)}
                  className={inputCls()}
                >
                  {allMarkets.map((m) => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                  <option value={ADD_CUSTOM}>+ Add Custom Market</option>
                </select>
              )}
            </Field>
            <Field label="Deal Stage">
              <select
                value={form.stage}
                onChange={(e) => set("stage", e.target.value)}
                className={inputCls()}
              >
                {STAGES.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </Field>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <Field label="Square Footage" error={errors.squareFeet} required>
              <input
                type="number"
                value={form.squareFeet}
                onChange={(e) => set("squareFeet", e.target.value)}
                placeholder="48500"
                className={inputCls(errors.squareFeet)}
              />
            </Field>
            <Field label="Asking Price ($)" error={errors.askingPrice}>
              <input
                type="number"
                value={form.askingPrice}
                onChange={(e) => set("askingPrice", e.target.value)}
                placeholder="11200000"
                className={inputCls(errors.askingPrice)}
              />
            </Field>
            <Field label="Cap Rate (%)" error={errors.capRate}>
              <input
                type="number"
                step="0.01"
                value={form.capRate}
                onChange={(e) => set("capRate", e.target.value)}
                placeholder="5.80"
                className={inputCls(errors.capRate)}
              />
            </Field>
          </div>

          {/* Underwriting Metrics — optional, fill in after UW */}
          <div>
            <label className="block text-xs font-semibold text-[#8a8a8a] uppercase tracking-wider mb-0.5">
              Underwriting Metrics
            </label>
            <p className="text-xs text-[#8a8a8a] mb-2">Optional — fill in after underwriting is complete.</p>
            <div className="grid grid-cols-3 gap-3 mb-3">
              <Field label="PPUSA Price ($)">
                <input type="number" value={form.underwrittenPrice}
                  onChange={(e) => set("underwrittenPrice", e.target.value)}
                  placeholder="Our price" className={inputCls()} />
              </Field>
              <Field label="Levered IRR (%)">
                <input type="number" step="0.1" value={form.leveredIrr}
                  onChange={(e) => set("leveredIrr", e.target.value)}
                  placeholder="e.g. 18.5" className={inputCls()} />
              </Field>
              <Field label="Equity Multiple (x)">
                <input type="number" step="0.01" value={form.equityMultiple}
                  onChange={(e) => set("equityMultiple", e.target.value)}
                  placeholder="e.g. 2.1" className={inputCls()} />
              </Field>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Trended YoC (%)">
                <input type="number" step="0.1" value={form.trendedYoc}
                  onChange={(e) => set("trendedYoc", e.target.value)}
                  placeholder="e.g. 7.5" className={inputCls()} />
              </Field>
              <Field label="Untrended YoC (%)">
                <input type="number" step="0.1" value={form.untrendedYoc}
                  onChange={(e) => set("untrendedYoc", e.target.value)}
                  placeholder="e.g. 6.8" className={inputCls()} />
              </Field>
              <Field label="Avg CoC (%)">
                <input type="number" step="0.1" value={form.averageCoc}
                  onChange={(e) => set("averageCoc", e.target.value)}
                  placeholder="e.g. 8.0" className={inputCls()} />
              </Field>
            </div>
          </div>

          {/* OM / Source Notes — distinct from team analysis fields below */}
          <div>
            <label className="block text-xs font-semibold text-[#8a8a8a] uppercase tracking-wider mb-1.5">
              OM / Source Notes
            </label>
            <div className="border-l-2 border-l-amber-500 pl-3">
              <p className="text-xs text-[#8a8a8a] mb-1.5">
                Auto-populated from OMs and external sources. Your team's analysis goes in the fields below.
              </p>
              <textarea
                rows={4}
                value={form.sourceNotes}
                onChange={(e) => set("sourceNotes", e.target.value)}
                placeholder="Notes extracted from the OM or other source documents will appear here."
                className={inputCls() + " resize-none"}
              />
            </div>
          </div>

          <Field label="Investment Thesis">
            <textarea
              rows={4}
              value={form.thesis}
              onChange={(e) => set("thesis", e.target.value)}
              placeholder="Why does this deal fit the PPUSA strategy? What's the value-add narrative?"
              className={inputCls() + " resize-none"}
            />
          </Field>

          <Field label="Open Questions">
            <textarea
              rows={3}
              value={form.openQuestions}
              onChange={(e) => set("openQuestions", e.target.value)}
              placeholder="What still needs to be answered before proceeding?"
              className={inputCls() + " resize-none"}
            />
          </Field>

          <Field label="Red Flags">
            <textarea
              rows={3}
              value={form.redFlags}
              onChange={(e) => set("redFlags", e.target.value)}
              placeholder="Known risks, concerns, or deal-killers to track."
              className={inputCls() + " resize-none"}
            />
          </Field>

          {/* Documents — queued here, uploaded after deal is saved */}
          <div>
            <label className="block text-xs font-semibold text-[#8a8a8a] uppercase tracking-wider mb-1.5">
              Documents
            </label>
            <UploadZone
              onFiles={(fileList) => setPendingFiles((prev) => [...prev, ...Array.from(fileList)])}
              uploading={false}
              progress={0}
            />
            {pendingFiles.length > 0 && (
              <div className="mt-2 flex flex-col gap-1">
                {pendingFiles.map((f, i) => (
                  <div key={i} className="flex items-center gap-2 bg-[#2c2c2c] border border-[rgba(58,58,58,0.4)] rounded-lg px-3 py-1.5">
                    <span className="text-sm leading-none">{fileIcon(f.type)}</span>
                    <span className="flex-1 text-xs text-[#b4b4b4] truncate">{f.name}</span>
                    <span className="text-xs text-[#8a8a8a]">{fmtFileSize(f.size)}</span>
                    {/* Remove from queue before saving */}
                    <button
                      type="button"
                      onClick={() => setPendingFiles((prev) => prev.filter((_, j) => j !== i))}
                      className="text-[#7a7a7a] hover:text-[#f04f44] text-xs ml-1 transition-colors"
                    >×</button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* A duplicate isn't an error — it's a question. Show which deals
              collided and what they are, so the choice is informed: open the
              existing one, or say this really is a separate deal. */}
          {saveError?.code === "duplicate_deal" ? (
            <div className="text-sm bg-[rgba(196,45,34,0.25)] border border-[rgba(196,45,34,0.4)] rounded-lg px-4 py-3.5 space-y-3">
              <div>
                <p className="font-semibold text-[#f04f44]">
                  Proxy already has {saveError.duplicates.length === 1 ? "a deal" : `${saveError.duplicates.length} deals`} that look like this
                </p>
                <p className="text-xs text-[rgba(244,130,122,0.7)] mt-0.5">
                  Nothing was saved yet.
                </p>
              </div>

              <div className="space-y-1.5">
                {saveError.duplicates.map(({ deal: d, reason }) => {
                  let addr = d.address;
                  try {
                    const parsed = JSON.parse(d.address);
                    addr = Array.isArray(parsed) ? parsed[0] : d.address;
                  } catch { /* legacy plain-string address */ }
                  return (
                    <div key={d.id} className="flex items-start justify-between gap-3 bg-[rgba(44,44,44,0.6)] rounded-md px-3 py-2">
                      <div className="min-w-0">
                        <p className="text-[#f4f4f4] text-[13px] font-medium truncate">
                          {parseDealName(d.source_notes) || addr || "(unnamed)"}
                        </p>
                        <p className="text-[11px] text-[#8a8a8a] truncate">
                          {addr} · {d.deal_stage}
                          {d.square_footage ? ` · ${Number(d.square_footage).toLocaleString()} SF` : ""}
                          {d.created_at ? ` · added ${String(d.created_at).slice(0, 10)}` : ""}
                        </p>
                        <p className="text-[11px] text-[rgba(240,79,68,0.8)] mt-0.5">{reason}</p>
                      </div>
                      {onOpenExisting && (
                        <button
                          type="button"
                          onClick={() => onOpenExisting(d.id)}
                          className="shrink-0 text-[11px] font-semibold px-2.5 py-1 rounded border border-[#4a4a4a] text-[#f4f4f4] hover:border-[#f04f44] hover:text-[#f04f44] transition-colors"
                        >
                          Open
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>

              <button
                type="button"
                onClick={() => { setAllowDuplicate(true); setSaveError(null); }}
                className="text-[12px] font-semibold text-[#f04f44] hover:text-[#f4827a] underline"
              >
                This is a different deal — let me save it
              </button>
            </div>
          ) : saveError ? (
            <p className="text-[#f04f44] text-sm bg-[rgba(196,45,34,0.3)] border border-[rgba(196,45,34,0.4)] rounded-lg px-4 py-2.5">
              <span className="font-semibold">Error: </span>{saveError}
            </p>
          ) : null}

          {allowDuplicate && (
            <p className="text-[12px] text-[rgba(240,79,68,0.8)]">
              Duplicate check overridden — press Save to add this as a separate deal.
            </p>
          )}

          <div className="flex gap-3 pt-2">
            <button
              onClick={handleSave}
              disabled={saving}
              className="bg-[#e93529] hover:bg-[#f04f44] disabled:opacity-50 disabled:cursor-not-allowed text-[#333333] font-semibold px-6 py-2.5 rounded-lg text-sm transition-colors"
            >
              {saving ? "Saving…" : "Save Deal"}
            </button>
            <button
              onClick={onCancel}
              disabled={saving}
              className="bg-[#3a3a3a] hover:bg-[#4a4a4a] disabled:opacity-50 text-[#f4f4f4] font-semibold px-6 py-2.5 rounded-lg text-sm transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}

// ─── Add Deal From OM ─────────────────────────────────────────────────────────

function AddDealFromOM({ onSave, onCancel, customMarkets, onAddCustomMarket }) {
  // "upload"    — show the drop zone
  // "extracting"— show spinner while reading + calling Claude
  // "review"    — show pre-filled AddDealForm
  const [step, setStep] = useState("upload");
  const [omFile, setOmFile] = useState(null);
  const [extractedValues, setExtractedValues] = useState(null);
  const [error, setError] = useState(null);
  // MSA list for the OM extraction prompt — so Claude knows what values to
  // snap "market" to. customMarkets is unioned in for session-scoped entries.
  const { msas: msaList } = useMsaList(customMarkets);
  const allMarkets = [...new Set([...msaList, ...customMarkets])];

  async function handleFile(fileList) {
    const file = fileList[0];
    if (!file) return;

    setOmFile(file);
    setStep("extracting");
    setError(null);

    try {
      const prompt = buildOMExtractionPrompt(allMarkets);
      let messages;

      if (file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf")) {
        // ── PDF: render pages to JPEG images, send as image content blocks ──
        // Sending a raw PDF document block fails for large OMs due to browser
        // payload limits on direct API calls. Rendering pages via canvas keeps
        // each image small and works reliably regardless of PDF size.
        const { images, totalPages, renderedPages } = await pdfToImages(file, 15);

        // Show a note if we couldn't include all pages, but don't block
        if (totalPages > renderedPages) {
          setError(`Large OM detected (${totalPages} pages). Reading the first ${renderedPages} pages for extraction.`);
        }

        const content = images.map((base64) => ({
          type: "image",
          source: { type: "base64", media_type: "image/jpeg", data: base64 },
        }));

        content.push({
          type: "text",
          text: totalPages > renderedPages
            ? `Extract the deal details from this Offering Memorandum. Note: only the first ${renderedPages} of ${totalPages} pages were included.`
            : "Extract the deal details from this Offering Memorandum.",
        });

        messages = [{ role: "user", content }];
      } else {
        // ── DOCX / XLSX / TXT: extract text first, send as plain message ────
        const text = await extractTextFromFile(file);

        if (!text || text.trim().length < 100) {
          throw new Error(
            "Couldn't extract enough text from this file. Make sure it's a Word doc or spreadsheet (not a scanned image)."
          );
        }

        messages = [{ role: "user", content: text.slice(0, 40000) }];
      }

      // Ask Claude to return structured JSON — 2048 tokens so the full JSON
      // (thesis, open questions, red flags) fits comfortably in the reply.
      const reply = await callClaudeWithMessages(prompt, messages, 2048);

      // Strip any accidental markdown fences before parsing
      const cleaned = reply.replace(/```json?\s*/gi, "").replace(/```/g, "").trim();
      const fields  = JSON.parse(cleaned);

      // Map to the form shape; snap market to a known value or fall back to "Other"
      // AI-extracted analysis goes into sourceNotes; team fields are left blank.
      const rawAddresses = fields.addresses;
      const addresses = Array.isArray(rawAddresses) && rawAddresses.length > 0
        ? rawAddresses.map(String)
        : [String(rawAddresses ?? "")];

      setExtractedValues({
        addresses,
        // Market is auto-derived from addresses on save (see insertDeal in supabase.js);
        // whatever the OM says is a hint that the user can override in review.
        market:      fields.market ? String(fields.market) : "",
        squareFeet:  fields.squareFeet  ? String(fields.squareFeet)  : "",
        askingPrice: fields.askingPrice ? String(fields.askingPrice) : "",
        capRate:     fields.capRate     ? String(fields.capRate)     : "",
        stage:       "Screening",
        sourceNotes: fields.sourceNotes
          ? `[Extracted from: ${file.name}]\n\n${String(fields.sourceNotes)}`
          : `[Extracted from: ${file.name}]`,
        thesis:        "",
        openQuestions: "",
        redFlags:      "",
      });

      setError(null); // clear any page-count warning on success
      setStep("review");

    } catch (err) {
      setError(err.message);
      setStep("upload");
    }
  }

  // Once in review mode, delegate entirely to the shared AddDealForm —
  // pass initialValues so it pre-fills, and omFile so it gets queued as a document.
  if (step === "review" && extractedValues) {
    return (
      <AddDealForm
        onSave={onSave}
        onCancel={onCancel}
        customMarkets={customMarkets}
        onAddCustomMarket={onAddCustomMarket}
        initialValues={extractedValues}
        omFile={omFile}
        omBanner={omFile?.name}
      />
    );
  }

  // Step: upload / extracting
  return (
    <div className="min-h-screen bg-[#242424] text-white flex flex-col">
      <header className="border-b border-[rgba(58,58,58,0.6)] bg-[rgba(36,36,36,0.95)] backdrop-blur sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center gap-4">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-md bg-[#e93529] flex items-center justify-center">
              <span className="text-[#333333] font-black text-sm">PP</span>
            </div>
            <span className="text-lg font-bold tracking-tight">
              PPUSA <span className="text-[#f04f44]">Deal Brain</span>
            </span>
          </div>
          <div className="h-4 w-px bg-[#3a3a3a]" />
          <span className="text-sm text-[#8a8a8a]">Upload OM</span>
        </div>
      </header>

      <main className="flex-1 flex flex-col items-center justify-center px-6 py-16">
        <div className="w-full max-w-lg flex flex-col gap-6">

          {step === "extracting" ? (
            /* ── Spinner state ── */
            <div className="flex flex-col items-center gap-5 py-12 text-[#8a8a8a]">
              <div className="flex gap-1.5">
                <span className="w-2.5 h-2.5 bg-[#e93529] rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                <span className="w-2.5 h-2.5 bg-[#e93529] rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                <span className="w-2.5 h-2.5 bg-[#e93529] rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
              </div>
              <div className="text-center">
                <p className="text-base font-semibold text-[#f4f4f4]">Reading OM…</p>
                <p className="text-sm text-[#8a8a8a] mt-1">
                  Sending <span className="text-[#b4b4b4]">{omFile?.name}</span> to Claude to identify the deal details.
                </p>
              </div>
            </div>
          ) : (
            /* ── Upload state ── */
            <>
              <div className="text-center">
                <p className="text-2xl font-bold text-white mb-1">Drop an Offering Memorandum</p>
                <p className="text-sm text-[#8a8a8a]">
                  PDF, Word, or Excel — we'll extract the deal details and pre-fill the form.
                </p>
              </div>

              {/* Reuse the shared UploadZone but give it more breathing room via a wrapper */}
              <div className="[&>div]:py-12 [&>div]:border-2">
                <UploadZone onFiles={handleFile} uploading={false} progress={0} />
              </div>

              {/* Error from a previous attempt */}
              {error && (
                <div className="bg-[rgba(196,45,34,0.3)] border border-[rgba(196,45,34,0.4)] rounded-xl px-4 py-3">
                  <p className="text-sm text-[#f4827a] font-semibold mb-1">Extraction failed</p>
                  <p className="text-xs text-[#f04f44]">{error}</p>
                  <p className="text-xs text-[#8a8a8a] mt-2">
                    Try a different file, or{" "}
                    <button
                      onClick={onCancel}
                      className="text-[#f04f44] hover:text-[#f04f44] underline"
                    >
                      add the deal manually
                    </button>.
                  </p>
                </div>
              )}

              <div className="text-center">
                <button
                  onClick={onCancel}
                  className="text-xs text-[#8a8a8a] hover:text-[#8a8a8a] transition-colors underline"
                >
                  Cancel — go back to pipeline
                </button>
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
}

// ─── Placeholder Pages ────────────────────────────────────────────────────────


export { AddDealForm, AddDealFromOM };
